/**
 * Centralized, hardcoded tooltip content for the PoS & Website admin.
 *
 * This is the SINGLE source of truth for every tooltip string in the app.
 * Components must NEVER hardcode tooltip text in JSX — they reference a
 * dot-path key from this object and let <InfoTooltip /> resolve it.
 *
 * Adding a new tooltip:
 *   1. Add a key (string or string[] for multi-line) under the relevant section.
 *   2. Reference it with <InfoTooltip contentKey="section.key" />.
 *   No component logic changes are required.
 *
 * A value may be:
 *   - a plain string (single line), or
 *   - a string[] (each entry rendered on its own line for multi-line help).
 */

export type TooltipContentValue = string | readonly string[]

export const tooltipContent = {
  /**
   * Purchase Order status badges. Keys MUST match the PurchaseOrder['status']
   * enum values so a tooltip can be resolved as `poStatus.${status}`.
   */
  poStatus: {
    Pending:
      'Initial stage before PO confirmation. Items and PO can still be edited or removed. Goods Receipt is not available yet.',
    Confirmed:
      'PO has been reviewed and confirmed. Goods Receipt can now be added. Items and PO can no longer be deleted.',
    PartiallyReceived:
      'Some items in this Purchase Order have been received from the vendor.',
    Completed:
      'All items in this Purchase Order have been successfully received.',
  },

  tickets: {
    list: 'View and manage submitted support tickets. Ticket types can be managed via Categories. Replies are supported.',
  },

  massChange: {
    list: 'Apply bulk updates to multiple inventory records at once.',
  },

  imageSlider: {
    main: 'Upload homepage hero images. Use the recommended image size and ratio for best display quality.',
  },

  banner: {
    list: 'Manage announcement banners displayed on the website. Create multiple messages, reorder them, and choose whether they appear on all pages or only the homepage.',
  },

  itemCategories: {
    list: 'Control category visibility on the website and review product counts for each category and sub-category.',
  },

  onlinePayment: {
    list: 'Review all website payment attempts and their current payment status.',
  },
} as const

type TooltipContent = typeof tooltipContent

/**
 * Union of every valid dot-path key in `tooltipContent`
 * (e.g. "poStatus.Pending" | "tickets.list" | ...).
 *
 * Derived automatically from the content object, so <InfoTooltip /> can only
 * be given keys that actually exist — a typo becomes a compile error rather
 * than a silently-empty tooltip. Adding a key to the object above
 * automatically widens this type; no manual maintenance required.
 */
export type TooltipContentKey = {
  [S in keyof TooltipContent & string]: `${S}.${keyof TooltipContent[S] & string}`
}[keyof TooltipContent & string]

/**
 * Safely resolve a dot-path key (e.g. "poStatus.Pending") against the
 * centralized content object.
 *
 * Fails gracefully: an unknown / missing / malformed key returns `undefined`
 * instead of throwing, so a typo can never crash the UI.
 */
export const resolveTooltipContent = (
  contentKey: string,
): TooltipContentValue | undefined => {
  if (!contentKey) return undefined

  const value = contentKey
    .split('.')
    .reduce<unknown>(
      (node, segment) =>
        node && typeof node === 'object'
          ? (node as Record<string, unknown>)[segment]
          : undefined,
      tooltipContent,
    )

  if (typeof value === 'string') return value
  if (Array.isArray(value) && value.every((line) => typeof line === 'string'))
    return value as readonly string[]

  return undefined
}
